import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment, Button, Loader, Dimmer } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import {
  getProduktkategorien,
  updateProduktkategorie,
  getVistPermissions,
  setBatchingParameters,
  resetBatchingParameters,
} from '../../../actions';
import { hasManagementUiAccess } from '../../../helpers/permissions';
import { BasicTable } from '../../ReactTableContent/ReactTableContent';

import { createColumnHelper } from '@tanstack/react-table';

import NavHeader from '../NavHeader';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const ProduktkategorienListing = ({
  pathname,
  produktkategorien,
  totalPages,
  getProduktkategorien,
  updateProduktkategorie,
  intl,
  token,
  permissions = null,
  getVistPermissions,
  setBatchingParameters,
  resetBatchingParameters,
  batchingParameters,
}) => {
  const currentListing = 'produktkategorien';
  const [error, setError] = useState(null);
  const [isClient, setIsClient] = useState(false);

  // table config
  const [tableConfig, setTableConfig] = useState({
    COLUMNS: [],
    showDeleted: false,
    data: [],
    totalPages: 0,
    linkToEdit: true,
  });

  const deletedButton = !tableConfig.showDeleted
    ? 'Gelöschte Kategorien anzeigen'
    : 'Kategorien anzeigen';

  const [refreshFlag, setRefreshFlag] = useState(false);

  const columnHelper = createColumnHelper();

  const COLUMNS = [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => <span>Bezeichnung</span>,
      cell: (info) => {
        const level = info.row.original.level;
        const isBold = level === 0 ? { fontWeight: 'bold' } : {};
        const indentationStyle = {
          paddingLeft: `${level}rem`,
          ...isBold,
        };

        return <p style={indentationStyle}>{info.getValue()}</p>;
      },
    }),
    columnHelper.accessor('crdate', {
      id: 'crdate',
      header: () => <span>Erstellt</span>,
      cell: (info) => {
        const timestamp = parseInt(info.getValue(), 10) * 1000; // Convert Unix timestamp to milliseconds
        const date = new Date(timestamp);

        // Format the date as "dd.mm.yy hh:mm"
        const formattedDate = `${padZero(date.getDate())}.${padZero(
          date.getMonth() + 1,
        )}.${padZero(date.getFullYear() % 100)}`;

        return <p>{formattedDate}</p>;
      },
    }),
    columnHelper.accessor('modified', {
      id: 'modified',
      header: () => <span>Bearbeitet</span>,
      cell: (info) => {
        const timestamp = parseInt(info.getValue(), 10) * 1000; // Convert Unix timestamp to milliseconds
        const date = new Date(timestamp);

        // Format the date as "dd.mm.yy hh:mm"
        const formattedDate = `${padZero(date.getDate())}.${padZero(
          date.getMonth() + 1,
        )}.${padZero(date.getFullYear() % 100)}`;

        if (timestamp > 0) {
          return <p>{formattedDate}</p>;
        } else {
          return <p></p>;
        }
      },
    }),
  ];

  const COLUMNS_DELETED = [
    ...COLUMNS,
    columnHelper.accessor('deleted', {
      id: 'deleted',
      header: () => <span></span>,
      cell: (info) => (
        <Button onClick={() => unDelete(info.row.original.id)}>
          Wiederherstellen
        </Button>
      ),
    }),
  ];

  // Helper function to pad zeroes for single-digit numbers
  const padZero = (num) => (num < 10 ? `0${num}` : num);
  // Management-table UI access: Admin + F&E per TASK-90 §2 and
  // "Produktkategorien ... kann F&E anlegen und Admins".
  const canViewListing = hasManagementUiAccess(permissions);
  // flag to make sure permissions are fully loaded before returning
  // an unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);

  // attatch produktkategorien to tableConfig when finished loading
  useEffect(() => {
    const columns = tableConfig.showDeleted ? COLUMNS_DELETED : COLUMNS;
    setTableConfig({
      ...tableConfig,
      data: produktkategorien,
      COLUMNS: columns,
      totalPages: totalPages,
    });
    if (
      batchingParameters &&
      batchingParameters.currentListing !== currentListing
    ) {
      resetBatchingParameters({ currentListing: currentListing, sort_by: '' });
      setRefreshFlag(!refreshFlag);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produktkategorien, tableConfig.showDeleted]);

  useEffect(() => {
    setIsClient(true);
    getProduktkategorien({
      showDeleted: tableConfig.showDeleted,
      batchingParameters: batchingParameters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    refreshFlag,
    getProduktkategorien,
    batchingParameters,
    tableConfig.showDeleted,
  ]);

  const toggleShowDeleted = () => {
    setTableConfig({
      ...tableConfig,
      showDeleted: !tableConfig.showDeleted,
    });
    resetBatchingParameters({ currentListing: currentListing, sort_by: '' });
    setRefreshFlag(!refreshFlag);
  };

  const unDelete = (id) => {
    updateProduktkategorie(id, { deleted: 0 });
    getProduktkategorien({
      showDeleted: true,
      batchingParameters: batchingParameters,
    });
    setRefreshFlag(!refreshFlag);
  };

  if (error) {
    return <Error error={error} />;
  }
  if (!token) {
    setError({ status: 401 });
  }
  if (!permissionsLoading && !canViewListing) {
    setError({ status: 401 });
  }
  return (
    (produktkategorien && (
      <div className="view-wrapper">
        <Helmet title="Produktkategorien" />
        <NavHeader />
        <Container className="controlpanel">
          <Segment.Group raised>
            <Segment className="primary">
              <div className="rezeptdatenbank header">
                <div className="header-container">
                  <h1>
                    {tableConfig.showDeleted
                      ? 'Gelöschte Produktkategorien'
                      : 'Produktkategorien'}
                  </h1>
                </div>
                <div className="button-container">
                  <div className="buttonArea">
                    <Link to="produktkategorien/add">
                      <Button className="rdb-new" primary type="New">
                        Neue Produktkategorie
                      </Button>
                    </Link>
                    <Button
                      className="rdb-show-deleted"
                      primary
                      onClick={() => toggleShowDeleted()}
                    >
                      {deletedButton}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="rezeptdatenbank header"></div>
            </Segment>
            {produktkategorien && tableConfig.data && (
              <Segment>
                <BasicTable
                  tableConfig={tableConfig}
                  setTableConfig={setTableConfig}
                  batchingParameters={batchingParameters}
                  setBatchingParameters={setBatchingParameters}
                />
              </Segment>
            )}
          </Segment.Group>
        </Container>
        {isClient && (
          <Portal node={document.getElementById('toolbar')}>
            <Toolbar
              pathname={pathname}
              hideDefaultViewButtons
              inner={
                <Link to="/controlpanel/produktdatenbank" className="item">
                  <Icon
                    name={backSVG}
                    className="contents circled"
                    size="30px"
                    title={intl.formatMessage(messages.back)}
                  />
                </Link>
              }
            />
          </Portal>
        )}
      </div>
    )) || (
      <Dimmer active>
        <Loader />
      </Dimmer>
    )
  );
};

ProduktkategorienListing.propTypes = {
  pathname: PropTypes.string.isRequired,
  produktkategorien: PropTypes.array.isRequired,
  totalPages: PropTypes.any.isRequired,
  getProduktkategorien: PropTypes.func.isRequired,
  updateProduktkategorie: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.object,
  getVistPermissions: PropTypes.func,
  setBatchingParameters: PropTypes.func,
  resetBatchingParameters: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      pathname: state.router.location.pathname,
      totalPages: state.produktkategorien.data.total_pages,
      produktkategorien: state.produktkategorien.data.produktkategorien,
      token: state.userSession.token,
      permissions: state.permissions.permissions,
      batchingParameters: state.batchingParameters,
    }),
    {
      getProduktkategorien,
      updateProduktkategorie,
      getVistPermissions,
      setBatchingParameters,
      resetBatchingParameters,
    },
  ),
)(ProduktkategorienListing);
